import * as Api from '@ViewModels';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

const BASE_QUERY_KEY = 'template';

export function useTemplate<T extends Api.ITemplate>({
	templateId,
	enabled,
	refetchOnWindowFocus,
	refetchOnMount,
	impersonationContext,
	forUserId,
}: {
	templateId: string;
	enabled?: boolean;
	refetchOnWindowFocus?: boolean;
	refetchOnMount?: boolean | 'always';
	impersonationContext?: Api.IImpersonationContext;
	forUserId?: string;
}) {
	const userSession = useUserSession();
	return useQuery({
		enabled: enabled !== false && !!templateId,
		refetchOnWindowFocus,
		refetchOnMount,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<T>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `template/${encodeURIComponent(templateId)}`,
					queryParams: forUserId ? { forUserId } : undefined,
				}),
				'GET'
			),
		queryKey: [BASE_QUERY_KEY, { templateId, forUserId, impersonationContextId: impersonationContext?.account?.id }],
	});
}

export function useSocialMediaSignature({
	forUserId,
	postCreatorUserId,
	impersonationContext,
	enabled = true,
}: {
	forUserId?: string;
	postCreatorUserId?: string;
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
}) {
	const userSession = useUserSession();

	return useQuery({
		enabled,
		refetchOnWindowFocus: false,
		staleTime: 0,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<Api.ITemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: 'template/socialMediaSignature',
					queryParams: forUserId ? { forUserId } : undefined,
				}),
				'GET'
			),
		retry: 0,
		queryKey: [
			'socialMediaSignature',
			{ forUserId, postCreatorUserId, impersonationContextId: impersonationContext?.account?.id },
		],
	});
}

export function useTemplateQueries<T extends Api.ITemplate>({
	templateIds,
	refetchOnWindowFocus,
	queryKeyProvider,
	impersonationContext,
	queryFnOverride,
}: {
	templateIds: string[];
	refetchOnWindowFocus?: boolean;
	queryKeyProvider?: (templateId: string, index: number) => string;
	impersonationContext?: Api.IImpersonationContext;
	/** Override the default query function for fetching templates. Used by admin to fetch cross-account templates via a different endpoint. */
	queryFnOverride?: (templateId: string, userSession: Api.UserSessionContext) => Promise<T>;
}) {
	const userSession = useUserSession();
	return useQueries({
		queries: templateIds.map((templateId, index) => {
			return {
				enabled: templateId != null,
				refetchOnWindowFocus,
				queryFn: () =>
					queryFnOverride
						? queryFnOverride(templateId, userSession)
						: userSession.webServiceHelper.callAsync<T>(
								Api.ImpersonationBroker.composeApiUrl({
									impersonationContext,
									urlPath: `template/${encodeURIComponent(templateId)}`,
									queryParams: {
										expand: 'LastUsedByDate',
									},
								}),
								'GET'
							),
				queryKey: [BASE_QUERY_KEY, { templateId }, queryKeyProvider?.(templateId, index)],
			};
		}),
	});
}
